import { useEffect, useState } from "react";
import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";
import type { Client } from "@/models/Dashboards";
import FormModal from "../common/HPFormModal";
import {
  Box,
  Alert,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HPInput from "../common/Input";
import DatasetSelector from "./chart-modal/DatasetSelector";
import { QuestionsList } from "./chart-modal/QuestionsList";
import BrandSelector from "./scorecard-modal/BrandSelector";
import { useScorecardModal } from "@/hooks/use-scorecard-modal";
import type {
  ScorecardBrand,
  ScorecardBrandCandidate,
  ScorecardSourceQuestion,
} from "@/models/Scorecard";

interface CreateScorecardModalProps {
  action: (
    datasetId: string,
    datasetVersionId: number | null,
    sourceQuestion: ScorecardSourceQuestion,
    brandCandidates: ScorecardBrandCandidate[],
    brands: ScorecardBrand[],
  ) => void;
  isCreatingChart?: boolean;
  hasActiveFilters?: boolean;
  existingDatasetIds?: string[];
  primaryClient?: Client;
  additionalClients?: Client[];
  dashboardId?: string;
}

const CreateScorecardModal: React.FC<CreateScorecardModalProps> = ({
  action,
  isCreatingChart = false,
  hasActiveFilters = false,
  existingDatasetIds = [],
  primaryClient,
  additionalClients = [],
  dashboardId,
}) => {
  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);

  const {
    datasetId,
    datasetVersionId,
    datasetGroups,
    datasetSearchTerm,
    isFetchingDatasets,
    datasetsHasMore,
    observerTargetDatasets,
    isAutoSelected,
    isDatasetRestricted,
    handleDatasetChange,
    handleDatasetSearchChange,

    questionSearchTerm,
    selectedQuestionId,
    filteredQuestions,
    isFetching,
    hasMore,
    isFetchingQuestionDetails,
    observerTarget,
    handleQuestionSearchChange,
    handleSourceQuestionSelect,

    sourceQuestion,
    brandCandidates,
    primaryBrandSourceId,
    competitorBrandSourceIds,
    handlePrimaryBrandChange,
    handleCompetitorsChange,
    brands,
    isCreateEnabled,
  } = useScorecardModal({
    hasActiveFilters,
    existingDatasetIds,
    primaryClient,
    additionalClients,
    dashboardId,
  });

  const handleCreate = () => {
    if (!sourceQuestion) return;
    action(datasetId, datasetVersionId, sourceQuestion, brandCandidates, brands);
  };

  // Collapse the question search once a Source Brand Question is chosen, to
  // free up space for the Brands* section below — re-expands if the dataset
  // changes (which clears the selection).
  const [questionAccordionExpanded, setQuestionAccordionExpanded] = useState(true);
  useEffect(() => {
    setQuestionAccordionExpanded(true);
  }, [datasetId]);
  useEffect(() => {
    if (sourceQuestion) setQuestionAccordionExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceQuestion?.questionId]);

  return (
    <FormModal
      title="New Scorecard"
      confirmButtonText="Continue"
      isConfirmButtonDisabled={!isCreateEnabled || isFetchingQuestionDetails}
      loading={isCreatingChart || isFetchingQuestionDetails}
      onConfirm={handleCreate}
      onCancel={closeTopModal}
    >
      <Box className="flex flex-col gap-2">
        {isDatasetRestricted && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Dataset selection is restricted to existing datasets because filters
            are currently applied to the dashboard. To add a chart from a
            different dataset, please reset the filters first.
          </Alert>
        )}

        <DatasetSelector
          datasetGroups={datasetGroups}
          selectedDatasetId={datasetId}
          isAutoSelected={isAutoSelected}
          searchTerm={datasetSearchTerm}
          onSearchChange={handleDatasetSearchChange}
          isFetching={isFetchingDatasets}
          hasMore={datasetsHasMore}
          observerTarget={observerTargetDatasets}
          onSelect={(id, versionId) => handleDatasetChange(id, versionId)}
        />

        {datasetId && (
          <Accordion
            expanded={questionAccordionExpanded}
            onChange={(_, isExpanded) => setQuestionAccordionExpanded(isExpanded)}
            sx={{ boxShadow: "none", border: "1px solid #eee" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" fontWeight={600}>
                  Source Brand Question*
                </Typography>
                {sourceQuestion && (
                  <Typography variant="caption" color="text.secondary">
                    {sourceQuestion.questionTitle}
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <HPInput
                label=""
                value={questionSearchTerm}
                onChange={handleQuestionSearchChange}
                placeholder="Search by label or title..."
              />

              {!isFetching && filteredQuestions.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  A Scorecard requires a categorical question that contains the
                  tracked brands: a Single or Multiple question with at least
                  two response options, or a Single grid question with at least
                  two Grid Variables and two Grid Values.
                </Alert>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <QuestionsList
                    questions={filteredQuestions}
                    selectedQuestionId={selectedQuestionId}
                    onQuestionSelect={handleSourceQuestionSelect}
                    searchTerm={questionSearchTerm}
                    isFetching={isFetching}
                    hasMore={hasMore}
                    observerTarget={observerTarget}
                  />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {sourceQuestion && brandCandidates.length > 0 && (
          <BrandSelector
            brandCandidates={brandCandidates}
            primaryBrandSourceId={primaryBrandSourceId}
            competitorBrandSourceIds={competitorBrandSourceIds}
            onPrimaryChange={handlePrimaryBrandChange}
            onCompetitorsChange={handleCompetitorsChange}
          />
        )}
      </Box>
    </FormModal>
  );
};

export default CreateScorecardModal;
